import { Directive, OnDestroy } from '@angular/core';
import { Exercicio, FuncaoService, GlobalService, Login, Relatorio } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AnexoLRFService } from '../service/anexo-lrf.service';

@Directive()
export class DividaConsolidadaLiquidaLRF implements OnDestroy {
  protected funcaoService: FuncaoService;
  protected globalService: GlobalService;
  private login: Login = new Login();
  protected unsubscribe: Subject<void> = new Subject();
  public valorDCL = 0;

  constructor(
    protected anexoServico: AnexoLRFService,
    protected mes: number,
    protected exercicio: Exercicio) {
    this.globalService = new GlobalService();
    this.funcaoService = new FuncaoService();
    this.login = GlobalService.obterSessaoLogin();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public async obterDividaConsolidada(orgaos: number[]) {
    const result = await this.anexoServico.obterDCL(this.mes, this.exercicio.id, orgaos).toPromise();
    return this.conteudo(result[0]);
  }

  // tslint:disable: max-line-length
  public montarRelatorio(orgaos: number[]) {
    this.anexoServico.obterDCL(this.mes, this.exercicio.id, orgaos)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dados => {
        Relatorio.imprimirPersonalizado(
          `DEMONSTRATIVO DA DÍVIDA CONSOLIDADA LÍQUIDA - EXCETO ÓRGÃO DE PREVIDÊNCIA`
          , this.login.usuario.nome,
          this.login.orgao.nome, this.login.brasao,
          this.conteudo(dados[0]),
          'portrait', 'DEMONSTRATIVO DA DÍVIDA CONSOLIDADA LÍQUIDA - EXCETO ÓRGÃO DE PREVIDÊNCIA',
          {
            linhas: {
              hLineWidth() {
                return 1;
              },
              vLineWidth() {
                return 1;
              },
              hLineColor() {
                return 'black';
              },
              paddingLeft() {
                return 3;
              },
              paddingRight() {
                return 3;
              }
            }
          });
      });
  }

  private conteudo(dados: any[]): {}[] {

    // monta o cabecalho
    const registros: {}[] = [
      [{
        text: `REFERÊNCIA: ` +
          this.globalService.obterDataBR().monthNames[this.mes - 1].toLocaleUpperCase() + '/' + this.exercicio.ano,
        alignment: 'center', bold: true, colSpan: 5
      }, '', '', '', ''],
      [{
        text: 'LRF, art. 55, inciso I, alínea "b"',
        alignment: 'left', bold: true, colSpan: 5
      }, '', '', '', ''],
      [{
        text: 'ESPECIFICAÇÃO',
        alignment: 'center',
        bold: true, fontSize: 7, rowSpan: 2, margin: 0
      }, {
        text: 'SALDO EXERCÍCIO ANTERIOR',
        alignment: 'center',
        bold: true, fontSize: 7, rowSpan: 2, margin: 0
      }, {
        text: `SALDO DO EXERCÍCIO DE ${this.exercicio.ano}`,
        alignment: 'center',
        bold: true, fontSize: 7, colSpan: 3
      }, '', ''],
      [{ text: '' }, { text: '' }, {
        text: 'Até o 1º Quadrimestre',
        alignment: 'center',
        bold: true, fontSize: 7, margin: 0
      }, {
        text: 'Até o 2º Quadrimestre',
        alignment: 'center',
        bold: true, fontSize: 7, margin: 0
      }, {
        text: 'Até o 3º Quadrimestre',
        alignment: 'center',
        bold: true, fontSize: 7, margin: 0
      }]
    ];

    // monta o agrupamento do relatório
    const grupos = this.funcaoService.agrupar(dados, 'titulo',
      ['anterior', 'quadrimestre1', 'quadrimestre2', 'quadrimestre3']);
    let totalDivida1 = 0;
    let totalDivida2 = 0;
    let totalDivida3 = 0;
    let totalDivida4 = 0;
    let totalDeducao1 = 0;
    let totalDeducao2 = 0;
    let totalDeducao3 = 0;
    let totalDeducao4 = 0;
    let totalContrib1 = 0;
    let totalContrib2 = 0;
    let totalContrib3 = 0;
    let totalContrib4 = 0;
    for (const grupo of grupos) {
      if (grupo.grupo === 'DÍVIDA CONSOLIDADA - DC (I)') {
        registros.push([
          {
            text: grupo.grupo, bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['anterior'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre1'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre2'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre3'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
        totalDivida1 = +grupo.totalizadores['anterior'];
        totalDivida2 = +grupo.totalizadores['quadrimestre1'];
        totalDivida3 = +grupo.totalizadores['quadrimestre2'];
        totalDivida4 = +grupo.totalizadores['quadrimestre3'];
      }
      if (grupo.grupo === 'DEDUÇÕES (II)¹') {
        for (const registro of grupo.registros) {
          if (registro.nome === '(-) Restos a Pagar Processados') {
            totalDeducao1 -= +registro.anterior;
            totalDeducao2 -= +registro.quadrimestre1;
            totalDeducao3 -= +registro.quadrimestre2;
            totalDeducao4 -= +registro.quadrimestre3;
          } else {
            totalDeducao1 += +registro.anterior;
            totalDeducao2 += +registro.quadrimestre1;
            totalDeducao3 += +registro.quadrimestre2;
            totalDeducao4 += +registro.quadrimestre3;
          }
        }
        registros.push([
          {
            text: grupo.grupo, bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(totalDeducao1, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(totalDeducao2, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(totalDeducao3, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(totalDeducao4, 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
      }
      if (grupo.grupo === 'RECEITA CORRENTE LÍQUIDA - RCL') {
        registros.push([
          {
            text: 'DÍV. CONSOLID. LÍQUIDA (DCL)=(I - II)', bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(totalDivida1 - totalDeducao1, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(totalDivida2 - totalDeducao2, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(totalDivida3 - totalDeducao3, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(totalDivida4 - totalDeducao4, 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
        if (this.mes <= 4) {
          this.valorDCL = (totalDivida2 - totalDeducao2);
        } else if (this.mes > 4 && this.mes <= 8) {
          this.valorDCL = (totalDivida3 - totalDeducao3);
        } else {
          this.valorDCL = (totalDivida4 - totalDeducao4);
        }
        registros.push([
          {
            text: grupo.grupo, bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['anterior'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre1'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre2'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre3'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
        registros.push([
          {
            text: '% da DC sobre a RCL', bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber((totalDivida1 / grupo.totalizadores['anterior']) * 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber((totalDivida2 / grupo.totalizadores['quadrimestre1']) * 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber((totalDivida3 / grupo.totalizadores['quadrimestre2']) * 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber((totalDivida4 / grupo.totalizadores['quadrimestre3']) * 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
        registros.push([
          {
            text: '% da DCL sobre a RCL', bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(((totalDivida1 - totalDeducao1) / grupo.totalizadores['anterior']) * 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(((totalDivida2 - totalDeducao2) / grupo.totalizadores['quadrimestre1']) * 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(((totalDivida3 - totalDeducao3) / grupo.totalizadores['quadrimestre2']) * 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(((totalDivida4 - totalDeducao4) / grupo.totalizadores['quadrimestre3']) * 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
        registros.push([
          {
            text: 'LIMITE DEFINIDO POR RESOLUÇÃO DO SENADO FEDERAL:  120%', bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber((grupo.totalizadores['anterior'] * 120) / 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber((grupo.totalizadores['quadrimestre1'] * 120) / 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber((grupo.totalizadores['quadrimestre2'] * 120) / 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber((grupo.totalizadores['quadrimestre3'] * 120) / 100, 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
        continue;
      }
      if (grupo.grupo === 'Parcelamentos de Dívidas') {
        registros.push([
          {
            text: 'Detalhamento da Dívida Contratual', bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['anterior'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre1'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre2'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre3'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
        // soma somante grupo de contribuições sociais
        for (const registro of grupo.registros) {
          if (registro.grupo === 'De Contribuições Sociais') {
            totalContrib1 += +registro.anterior;
            totalContrib2 += +registro.quadrimestre1;
            totalContrib3 += +registro.quadrimestre2;
            totalContrib4 += +registro.quadrimestre3;
          }
        }

        registros.push([
          {
            text: grupo.grupo, bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['anterior'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre1'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre2'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre3'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
      }
      if (grupo.grupo === 'OUTROS VALORES NÃO INTEGRANTES DA DC') {
        registros.push([
          {
            text: grupo.grupo, bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['anterior'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre1'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre2'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores['quadrimestre3'], 2, true), alignment: 'right', bold: true, fontSize: 7 },
        ]);
      }
      // ---
      for (const registro of grupo.registros) {
        if (registro.nome === 'Previdenciárias') {
          registros.push([
            {
              text: 'De Contribuições Sociais', fontSize: 7, bold: true
            },
            { text: this.funcaoService.convertToBrNumber(totalContrib1, 2, true), alignment: 'right', fontSize: 7, bold: true },
            { text: this.funcaoService.convertToBrNumber(totalContrib2, 2, true), alignment: 'right', fontSize: 7, bold: true },
            { text: this.funcaoService.convertToBrNumber(totalContrib3, 2, true), alignment: 'right', fontSize: 7, bold: true },
            { text: this.funcaoService.convertToBrNumber(totalContrib4, 2, true), alignment: 'right', fontSize: 7, bold: true },
          ]);
        }
        registros.push([
          {
            text: '  ' + registro.nome, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(registro.anterior, 2, true), alignment: 'right', fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(registro.quadrimestre1, 2, true), alignment: 'right', fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(registro.quadrimestre2, 2, true), alignment: 'right', fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(registro.quadrimestre3, 2, true), alignment: 'right', fontSize: 7 },
        ]);
      }
    }

    return [{
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        headerRows: 2,
        widths: ['*', 80, 80, 80, 80],
        body: registros
      }
    }];
  }

}
